import React, { useCallback, useEffect, useRef, useState } from "react";
import Toast, { type ToastVariant } from "../components/Toast";
import {
  subscribeAppToast,
  REPORT_ISSUE_TOAST_LABEL,
  type AppToastPayload,
  type AppToastPlacement,
  type AppToastType,
} from "../utils/appToast";
import {
  EDITOR_BOTTOM_TOAST_OFFSET_PX,
  EDITOR_TOAST_Z_INDEX,
  resolveEditorViewportCenterLeft,
} from "../utils/editor/toast";

import { useReportIssueToast } from "../hooks/common/useReportIssueToast";

interface AppToastProviderProps {
  children: React.ReactNode;
}

/**
 * Renders a single global toast and registers `appToast()` listener.
 */
export const AppToastProvider: React.FC<AppToastProviderProps> = ({
  children,
}) => {
  const [message, setMessage] = useState("");
  const [type, setType] = useState<AppToastType>("notice");
  const [duration, setDuration] = useState<number | null>(3000);
  const [actionLabel, setActionLabel] = useState<string | undefined>();
  const [onAction, setOnAction] = useState<(() => void) | undefined>();
  const [editorProjectToast, setEditorProjectToast] = useState(false);
  const [placement, setPlacement] = useState<AppToastPlacement | undefined>();
  const [editorViewportLeftPad, setEditorViewportLeftPad] = useState("0px");
  const currentPayloadRef = useRef<AppToastPayload | null>(null);

  const [open, setOpen] = useReportIssueToast(type === "error" && actionLabel === REPORT_ISSUE_TOAST_LABEL);

  const show = useCallback((payload: AppToastPayload | null) => {
    if (!payload) {
      const current = currentPayloadRef.current;
      currentPayloadRef.current = null;
      setEditorProjectToast(false);
      current?.onDismiss?.();
      setOpen(false);
      return;
    }
    currentPayloadRef.current = payload;
    setEditorProjectToast(payload.editorProject !== undefined);
    setPlacement(payload.placement);
    setMessage(payload.message);
    setType(payload.type);
    setDuration(payload.duration);
    setActionLabel(payload.actionLabel);
    setOnAction(() => payload.onAction);
    setOpen(true);
  }, [setOpen]);

  useEffect(() => {
    subscribeAppToast(show);
    return () => {
      subscribeAppToast(null);
    };
  }, [show]);

  useEffect(() => {
    const handleViewportPadding = (event: Event) => {
      const left = (event as CustomEvent<{ left?: unknown }>).detail?.left;
      setEditorViewportLeftPad(String(left ?? "0px"));
    };
    window.addEventListener("unity:viewportPadding", handleViewportPadding);
    return () =>
      window.removeEventListener("unity:viewportPadding", handleViewportPadding);
  }, []);

  const handleClose = useCallback(() => {
    const current = currentPayloadRef.current;
    currentPayloadRef.current = null;
    setEditorProjectToast(false);
    current?.onDismiss?.();
    setOpen(false);
  }, [setOpen]);

  const handleAction = useCallback(() => {
    currentPayloadRef.current = null;
    setEditorProjectToast(false);
    setOpen(false);
    onAction?.();
  }, [onAction, setOpen]);

  const variant: ToastVariant = type === "notice" ? "no-icon" : type;
  const editorAnchored = editorProjectToast || placement === "editorViewport";
  return (
    <>
      {children}
      <Toast
        open={open}
        message={message}
        variant={variant}
        duration={duration}
        onClose={handleClose}
        actionLabel={actionLabel}
        onAction={onAction ? handleAction : undefined}
        showCloseButton={type === "error" || Boolean(currentPayloadRef.current?.onDismiss)}
        sx={
          editorAnchored
            ? {
                // project-scoped Editor toast는 열린 dialog 조작을 가리지 않도록
                // modal 아래 layer를 쓴다. 명시적 editorViewport 요청은 modal이
                // 열린 채로 떠야 하는 알림이라 기본 snackbar layer를 유지한다.
                ...(editorProjectToast ? { zIndex: EDITOR_TOAST_Z_INDEX } : {}),
                bottom: `${EDITOR_BOTTOM_TOAST_OFFSET_PX}px !important`,
                left: `${resolveEditorViewportCenterLeft(editorViewportLeftPad)} !important`,
                transform: "translateX(-50%) !important",
              }
            : undefined
        }
      />
    </>
  );
};
